import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const THEMES = {
  emerald: {
    overlay: "from-slate-900/85 via-slate-900/60 to-transparent",
    accent: "text-emerald-400",
    btn: "bg-emerald-500 hover:bg-emerald-400 text-slate-900",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
  },
  slate: {
    overlay: "from-slate-900/90 via-slate-800/60 to-transparent",
    accent: "text-sky-400",
    btn: "bg-white hover:bg-slate-100 text-slate-900",
    badge: "bg-white/10 text-white border-white/20",
  },
  amber: {
    overlay: "from-amber-900/85 via-amber-800/50 to-transparent",
    accent: "text-amber-300",
    btn: "bg-amber-400 hover:bg-amber-300 text-slate-900",
    badge: "bg-amber-500/25 text-amber-100 border-amber-300/40",
  },
};

export default function HeroCarousel() {
  const [banners, setBanners] = useState([]);
  const [autoplayMs, setAutoplayMs] = useState(5000);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data || []));
    api.get("/settings").then((r) => setAutoplayMs(r.data?.carousel_autoplay_ms || 5000)).catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (banners.length ? (i + 1) % banners.length : 0)), [banners.length]);
  const prev = useCallback(() => setIdx((i) => (banners.length ? (i - 1 + banners.length) % banners.length : 0)), [banners.length]);

  useEffect(() => {
    if (paused || banners.length < 2) return;
    const id = setInterval(next, Math.max(2000, autoplayMs));
    return () => clearInterval(id);
  }, [paused, autoplayMs, next, banners.length]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 40) delta < 0 ? next() : prev();
    touchStart.current = null;
  };

  if (!banners.length) {
    return <div className="w-full h-[280px] sm:h-[360px] lg:h-[440px] bg-slate-100 animate-pulse" />;
  }

  return (
    <section
      className="relative w-full overflow-hidden bg-slate-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="hero-carousel"
    >
      <div
        ref={trackRef}
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {banners.map((b, i) => {
          const theme = THEMES[b.theme || "emerald"] || THEMES.emerald;
          return (
            <div
              key={b.id}
              className="relative shrink-0 w-full h-[280px] sm:h-[360px] lg:h-[440px]"
              data-testid={`banner-${i + 1}`}
            >
              <img
                src={b.image_url}
                alt={b.headline}
                loading={i === 0 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className={`absolute inset-0 bg-gradient-to-r ${theme.overlay}`} />
              <div className="relative blz-container h-full flex items-center">
                <div className="max-w-xl text-white">
                  {b.badge && (
                    <div className={`inline-block text-[10px] sm:text-xs font-semibold uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border ${theme.badge}`}>
                      {b.badge}
                    </div>
                  )}
                  <h2 className={`font-display font-extrabold text-3xl sm:text-4xl lg:text-6xl mt-4 leading-[1.05] tracking-tight`}>
                    {b.headline}
                  </h2>
                  {b.subtext && (
                    <p className="mt-4 text-sm sm:text-base lg:text-lg text-slate-200 max-w-lg">
                      {b.subtext}
                    </p>
                  )}
                  {b.cta_label && (
                    <Link
                      to={b.cta_href || "/catalogue"}
                      className={`mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold text-sm tracking-wide ${theme.btn} transition`}
                      data-testid={`banner-cta-${i + 1}`}
                    >
                      {b.cta_label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {banners.length > 1 && (
        <>
          <button
            aria-label="Previous"
            onClick={prev}
            className="hidden sm:grid place-items-center absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/85 hover:bg-white text-slate-900 shadow-lg transition"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            aria-label="Next"
            onClick={next}
            className="hidden sm:grid place-items-center absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/85 hover:bg-white text-slate-900 shadow-lg transition"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to banner ${i + 1}`}
                onClick={() => setIdx(i)}
                className={`h-2 rounded-full transition-all ${i === idx ? "bg-white w-8" : "bg-white/40 w-2 hover:bg-white/70"}`}
                data-testid={`carousel-dot-${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
